import { ButtonList, Icon } from '@dixa/lib-shared-habla';
import { useSortable } from '@dnd-kit/sortable';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Toolbar } from 'articleEditor/components/toolbar';
import { MenuOption } from 'articleEditor/utils/actions';
import cx from 'classnames';
import { deselect } from 'lib-knowledge-document-structure/plate/common';
import {
  MyEditor,
  MyRootBlock,
} from 'lib-knowledge-document-structure/plateTypes';
import {
  BaseSelection,
  Path,
} from 'lib-knowledge-document-structure/slate/package';
import { useEffect, useState } from 'react';

import s from '../block.module.scss';
import { BlockMenuItem } from './blockMenuItem';

type BlockMenuProps = {
  sortable: ReturnType<typeof useSortable>;
  editor: MyEditor;
  element: MyRootBlock;
  path: Path;
  blockActions: ReadonlyArray<MenuOption>;
  selectedActionIndex?: number;
  showingMenu: boolean;
  setShowingMenu: (isShowing: boolean) => void;
};

export const BlockMenu = ({
  editor,
  element,
  path,
  blockActions,
  showingMenu,
  setShowingMenu,
  sortable,
}: BlockMenuProps): JSX.Element => {
  // So we can remove the selection when we open the menu, which removes focus from the editor.
  const [editorSelection, setEditorSelection] = useState<BaseSelection | null>(
    null,
  );

  // Hide the menu if we start dragging a block.
  const isDragging = sortable.isDragging;
  useEffect(() => {
    if (isDragging && showingMenu) {
      setShowingMenu(false);
    }
  }, [isDragging, showingMenu, setShowingMenu]);

  // Run the selected action
  const onChange = (menuItem: MenuOption) => {
    if ('action' in menuItem) {
      menuItem.action({
        editor,
        element,
        path,
        selection: editorSelection,
      });
    }
  };

  return (
    <DropdownMenu.Root open={showingMenu} onOpenChange={setShowingMenu}>
      <DropdownMenu.Trigger
        className={cx(s['blockButton'], s['blockButtonDrag'])}
        tabIndex={-1}
        style={{ cursor: 'grab' }}
        {...sortable.listeners}
        onPointerUp={() => {
          setEditorSelection(editor.selection);
          deselect(editor);
          setShowingMenu(true);
        }}
        onPointerDown={(e) => {
          sortable.listeners?.onPointerDown(e);
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Icon className={s['blockIcon']} name="drag" size={20} />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content asChild sideOffset={5} side="left">
          <Toolbar>
            <DropdownMenu.Group asChild>
              <ButtonList>
                {blockActions.map((o, i) => (
                  <BlockMenuItem
                    key={`${o.key}_${i}`}
                    menuItem={o}
                    onSelect={onChange}
                  />
                ))}
              </ButtonList>
            </DropdownMenu.Group>
          </Toolbar>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
